import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { scaleLinear } from 'd3-scale';
import { format as d3Format } from 'd3-format';
import {
    Alert,
    Paper,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DotWaveLoader from './commonComponents/DotWaveLoader';
import SortControl from "./group_risk_metrics/SortControl";
import {
    ResponsiveContainer,
    BarChart,
    XAxis,
    YAxis,
    CartesianGrid,
    ReferenceLine,
    Tooltip,
    Bar,
    Text,
} from 'recharts';
import { posSizingTableTitleSx } from './pos_sizing_table/tableStyles';
import { cardClasses } from '../styles/tailwindClasses';
import {
    ACTUAL_BAR_COLOR,
    OPTIMIZED_BAR_COLOR,
    getRiskOverviewChartChrome,
    getRiskOverviewTooltipStyle,
    riskOverviewPanelSx,
} from '../utils/riskOverviewStyles';
import { usesAppStyleGrammar } from '../styles/msiStyleTokens';
import {
    buildFactorExposureModel,
    getChartExtent,
    selectFactorChartRows,
} from '../utils/riskOverviewChartPresentation';

const EMPTY_ROWS = Object.freeze([]);
const FACTOR_ROW_HEIGHT = 32;

function FactorAxisTick({ x, y, payload, width, fill, fontSize }) {
    return (
        <g>
            <title>{payload.value}</title>
            <Text
                x={x}
                y={y}
                width={width - 16}
                textAnchor="end"
                verticalAnchor="middle"
                maxLines={2}
                lineHeight={14}
                fill={fill}
                fontSize={fontSize}
            >
                {payload.value}
            </Text>
        </g>
    );
}

// SI‐style formatting
const siFormatAxis    = d3Format('.2~s');
const siFormatTooltip = d3Format('.3~s');
function formatAbbrevAxis(v)    { return siFormatAxis(v).replace(/G$/,'B').replace(/k$/,'T'); }
function formatAbbrevTooltip(v){ return siFormatTooltip(v).replace(/G$/,'B').replace(/k$/,'T'); }
function CustomTooltip({ active, payload, label, metricLabel, theme }) {
    if (!active || !payload?.length) return null;
    const actual = payload.find(p => p.dataKey==='value')?.value;
    const opt    = payload.find(p => p.dataKey==='optValue')?.value;
    const hasActual = typeof actual==='number';
    const hasOpt = typeof opt==='number';
    let pctChange = '';
    if (hasActual && hasOpt && actual!==0) {
        const raw = ((opt-actual)/Math.abs(actual))*100;
        pctChange = ` (${raw>=0?'+':''}${raw.toFixed(2)}%)`;
    }
    return (
        <div style={getRiskOverviewTooltipStyle(theme)}>
            <strong>{metricLabel} – {label}</strong>
            <div style={{ color: usesAppStyleGrammar(theme) ? ACTUAL_BAR_COLOR : theme.palette.info.main }}>
                Actual: {hasActual ? formatAbbrevTooltip(actual) : '—'}
            </div>
            {hasOpt && (
                <div style={{ color: usesAppStyleGrammar(theme) ? OPTIMIZED_BAR_COLOR : theme.palette.success.main }}>
                    Optimized: {formatAbbrevTooltip(opt)}{pctChange}
                </div>
            )}
        </div>
    );
}

export default function FactorExposureChart({
    calculations = {},
    optimizedCalculations = {},
    emptyPortfolio = false,
    isLoading = false,
}) {
    const theme = useTheme();
    const compactLabels = useMediaQuery(theme.breakpoints.down('sm'));
    const labelWidth = compactLabels ? 180 : 260;
    const chartChrome = getRiskOverviewChartChrome(theme);
    const actualExposures = calculations.factorExposures || EMPTY_ROWS;
    const optimizedExposures = optimizedCalculations?.factorExposures || EMPTY_ROWS;
    const factorModel = useMemo(
        () => buildFactorExposureModel(actualExposures, optimizedExposures),
        [actualExposures, optimizedExposures],
    );
    const { rows: merged, hasOptimized: hasOpt } = factorModel;

    const [sortKey,   setSortKey]   = useState('actual');    // 'sector'|'actual'|'optimized'
    const [sortOrder, setSortOrder] = useState('desc');

    const [selectedFactors, setSelectedFactors] = useState(factorModel.defaultFactorNames);
    const previousFactorDigestRef = useRef(factorModel.factorNameDigest);

    useEffect(() => {
        if (previousFactorDigestRef.current === factorModel.factorNameDigest) return;
        previousFactorDigestRef.current = factorModel.factorNameDigest;
        setSelectedFactors(factorModel.defaultFactorNames);
    }, [factorModel.defaultFactorNames, factorModel.factorNameDigest]);

    const chartData = useMemo(() => {
        return selectFactorChartRows({
            rows: merged,
            selectedFactors,
            sortKey,
            sortOrder,
        });
    }, [merged, selectedFactors, sortKey, sortOrder]);

    const { min: minVal, max: maxVal } = useMemo(
        () => getChartExtent(chartData, hasOpt),
        [chartData, hasOpt],
    );
    const ticks = useMemo(()=>scaleLinear().domain([minVal,maxVal]).nice(10).ticks(10),[minVal,maxVal]);

    const showLoading = isLoading;
    if (emptyPortfolio || (!merged.length && !showLoading)) {
        return (
            <Paper
                className={cardClasses}
                elevation={1}
                sx={{
                    ...riskOverviewPanelSx,
                    p: 2,
                }}
            >
                <Typography variant="subtitle1" sx={posSizingTableTitleSx}>
                    Factor Exposure with STD Move ($)
                </Typography>
                <Alert
                    data-testid="factor-exposure-empty-state"
                    severity="info"
                    sx={{ mt: 1 }}
                    aria-label="No factor exposures"
                >
                    Load a portfolio to view factor exposure.
                </Alert>
            </Paper>
        );
    }
    return (
        <Paper
            className={cardClasses}
            elevation={0}
            sx={{
                ...riskOverviewPanelSx,
                p: 2,
                height: { xs: 520, md: 600 },
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap: 'wrap', gap: 1.5, mb:2, px:1 }}>
                <Box sx={{ display:'flex', alignItems:'center' }}>
                    <Typography variant="subtitle1" sx={posSizingTableTitleSx}>
                        Factor Exposure with STD Move ($)
                    </Typography>
                </Box>
                {!showLoading && <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <SortControl
                        sortKey={sortKey}
                        sortOrder={sortOrder}
                        showOptimized={hasOpt}
                        onChange={(k,o)=>{ setSortKey(k); setSortOrder(o); }}
                    />
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                        <Box sx={{ width:12, height:12, bgcolor:ACTUAL_BAR_COLOR }} />
                        <Typography variant="caption">Actual</Typography>
                        {hasOpt && (
                            <>
                                <Box sx={{ width:12, height:12, bgcolor:OPTIMIZED_BAR_COLOR }} />
                                <Typography variant="caption">Optimized</Typography>
                            </>
                        )}
                    </Box>
                    <FormControl size="small" sx={{ width: { xs: '100%', sm: 220 } }}>
                        <InputLabel id="factor-exposure-select-label">Choose factors to visualize</InputLabel>
                        <Select
                            labelId="factor-exposure-select-label"
                            multiple
                            value={selectedFactors}
                            onChange={e=>setSelectedFactors(e.target.value)}
                            renderValue={vals =>
                                vals.length<5
                                    ? vals.join(', ')
                                    : `${vals.length} selected`
                            }
                            label="Factors"
                        >
                            {factorModel.orderedFactorNames.map(f=>(
                                <MenuItem key={f} value={f}>
                                    <Checkbox checked={selectedFactors.includes(f)} />
                                    <ListItemText primary={f} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>}
            </Box>

            {showLoading ? (
                <Box
                    role="status"
                    aria-busy="true"
                    aria-label="Loading factor exposures"
                    sx={{ flex: 1, minHeight: 0, p: 2 }}
                >
                    <DotWaveLoader height="100%" decorative />
                </Box>
            ) : <Box
                role="region"
                aria-label="Factor exposure chart"
                tabIndex={0}
                sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
            >
                <ResponsiveContainer
                    width="100%"
                    height={Math.max(360, chartData.length * FACTOR_ROW_HEIGHT + 60)}
                >
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top:8, right:20, left:0, bottom:20 }}
                        barSize={18}
                        barCategoryGap={0}
                        barGap={-12}
                    >
                        <CartesianGrid stroke={chartChrome.grid} strokeDasharray="3 3" horizontal={false} />
                        <XAxis
                            type="number"
                            domain={[minVal, maxVal]}
                            ticks={ticks}
                            tickFormatter={formatAbbrevAxis}
                            tick={usesAppStyleGrammar(theme)
                                ? { fill: chartChrome.tick, fontSize: chartChrome.tickFontSize }
                                : undefined}
                            axisLine={usesAppStyleGrammar(theme) ? { stroke: chartChrome.axis } : undefined}
                            tickLine={usesAppStyleGrammar(theme) ? { stroke: chartChrome.axis } : undefined}
                        />
                        <ReferenceLine
                            x={0}
                            stroke={usesAppStyleGrammar(theme)
                                ? chartChrome.reference
                                : 'rgb(var(--c-border))'}
                        />
                        <YAxis
                            dataKey="factor"
                            type="category"
                            width={labelWidth}
                            tickLine={false}
                            axisLine={false}
                            interval={0}
                            tick={<FactorAxisTick
                                width={labelWidth}
                                fill={usesAppStyleGrammar(theme)
                                    ? chartChrome.tick
                                    : theme.palette.text.primary}
                                fontSize={chartChrome.tickFontSize}
                            />}
                        />
                        <Tooltip
                            cursor={{
                                fill: chartChrome.cursor
                                    || alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.22 : 0.08),
                            }}
                            content={<CustomTooltip metricLabel="Factor Exp." theme={theme} />}
                        />
                        <Bar
                            dataKey="value"
                            name="Actual"
                            fill={ACTUAL_BAR_COLOR}
                            fillOpacity={0.4}
                            radius={0}
                            isAnimationActive={false}
                        />
                        {hasOpt && (
                            <Bar
                                dataKey="optValue"
                                name="Optimized"
                                barSize={8}
                                fill={OPTIMIZED_BAR_COLOR}
                                fillOpacity={0.5}
                                radius={0}
                                isAnimationActive={false}
                            />
                        )}
                    </BarChart>
                </ResponsiveContainer>
            </Box>}
        </Paper>
    );
}

const factorExposureShape = PropTypes.shape({
    factor: PropTypes.string,
    factor_exposure_3std_swing: PropTypes.number,
});

FactorExposureChart.propTypes = {
    calculations: PropTypes.shape({
        factorExposures: PropTypes.arrayOf(factorExposureShape),
    }),
    optimizedCalculations: PropTypes.shape({
        factorExposures: PropTypes.arrayOf(factorExposureShape),
    }),
    emptyPortfolio: PropTypes.bool,
    isLoading: PropTypes.bool,
};
